import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { CurrencyItemProps } from '../components/CurrencyItem'
import { useGetWallet, WalletCurrency } from '@/domain'
import { useGetBlockchainsCatalogs } from '@/domain/blockchains'
import { useAddMoneyStore } from '@/store/useAddMoneyStore'
import { useUserStore } from '@/store/useUserStore'

export const useCurrencySelection = () => {
  const navigate = useNavigate()
  const [selectedCurrency, setSelectedCurrency] =
    useState<WalletCurrency | null>(null)
  const [walletClickCount, setWalletClickCount] = useState(0)
  const [isBlockchainModalOpen, setIsBlockchainModalOpen] = useState(false)

  const { setSelectedCurrency: setStoreSelectedCurrency } = useAddMoneyStore()

  // Получаем данные кошелька
  const {
    data: walletData,
    isLoading: isWalletLoading,
    isError: isWalletError,
  } = useGetWallet()

  // Получаем валюты из store (как запасной вариант)
  const completeCurrencies = useUserStore((s) => s.wallet?.completeCurrencies)

  // Получаем актуальные валюты из кошелька или store
  // В RN версии для select-currency НЕ используется фильтрация - берутся все валюты
  const currencyList =
    walletData?.completeCurrencies || completeCurrencies || []

  // API запрос для получения блокчейнов выбранной валюты
  const { data: blockchains, isSuccess: hasSuccessOnLoadBlockchains } =
    useGetBlockchainsCatalogs(
      { currency_isin: selectedCurrency?.code || '' },
      { enabled: !!selectedCurrency },
    )

  // Функция для перехода на страницу депозита (аналог redirectToCurrencyDepositDetailsPage)
  const navigateToCurrencyDeposit = (
    currency: WalletCurrency,
    selectedBlockchain?: any,
  ) => {
    navigate('/currency-deposit', {
      state: {
        currency,
        blockchains: blockchains?.blockchains || [],
        selectedBlockchain,
      },
    })
  }

  // Обработчик выбора блокчейна из BottomSheet
  const handleBlockchainSelect = (blockchain: any) => {
    setIsBlockchainModalOpen(false)

    if (selectedCurrency) {
      navigateToCurrencyDeposit(selectedCurrency, blockchain)
    }
  }

  // Функция для закрытия BottomSheet
  const closeBlockchainModal = () => {
    setIsBlockchainModalOpen(false)
  }

  // Effect для автоматического перехода/показа модалки после загрузки блокчейнов
  // Аналог useEffect из RN версии (строки 82-91)
  useEffect(() => {
    if (hasSuccessOnLoadBlockchains && blockchains && selectedCurrency) {
      if (blockchains.blockchains?.length === 1) {
        // Если блокчейн один - сразу переходим
        navigateToCurrencyDeposit(selectedCurrency, blockchains.blockchains[0])
      } else if (blockchains.blockchains?.length > 1) {
        // Если блокчейнов несколько - показываем модалку
        setIsBlockchainModalOpen(true)
      } else {
        // Переходим без блокчейна
        navigateToCurrencyDeposit(selectedCurrency, undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSuccessOnLoadBlockchains, blockchains, walletClickCount])

  // Обработчик клика на валюту (аналог onPress в RN, строки 102-105)
  const handleCurrencyPress = (currency: WalletCurrency) => {
    setSelectedCurrency(currency)
    setWalletClickCount((prev) => prev + 1)

    // Сохраняем выбранную валюту в store
    setStoreSelectedCurrency(currency)
  }

  // Преобразуем валюты в формат для компонента
  const currencies = useMemo<CurrencyItemProps[]>(() => {
    return currencyList.map((currency) => ({
      ...currency,
      onPress: (currencyData?: WalletCurrency) => {
        if (currencyData) {
          handleCurrencyPress(currencyData)
        }
      },
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyList])

  return {
    currencies,
    selectedCurrency,
    handleCurrencySelect: handleCurrencyPress,
    navigateToCurrencyDeposit,
    isLoading: isWalletLoading,
    isError: isWalletError,
    // BottomSheet управление
    isBlockchainModalOpen,
    closeBlockchainModal,
    handleBlockchainSelect,
    blockchains: blockchains?.blockchains || [],
  }
}
